/**
 * ConnectionStatus - Visual indicator for SignalR connection status
 * 
 * Displays current connection state with color-coded icons and quality metrics.
 * Supports three states: Connected (green), Reconnecting (yellow), Disconnected (red).
 * 
 * @author BizClick WMS
 * @version 1.0 - Fase 2 Implementation
 */

import React from 'react';
import { Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { ConnectionStatus as Status, ConnectionQuality } from '../../types/locationActivity';

interface ConnectionStatusProps {
  status: Status;
  quality: ConnectionQuality;
  className?: string;
}

/**
 * Get status color class based on connection state
 */
const getStatusColor = (status: Status): string => {
  switch (status) {
    case Status.Connected:
      return 'text-green-500';
    case Status.Reconnecting:
      return 'text-yellow-500';
    case Status.Disconnected:
      return 'text-red-500';
    default:
      return 'text-gray-500';
  }
};

/**
 * Get status background color class
 */
const getStatusBgColor = (status: Status): string => {
  switch (status) {
    case Status.Connected:
      return 'bg-green-500/10';
    case Status.Reconnecting:
      return 'bg-yellow-500/10';
    case Status.Disconnected:
      return 'bg-red-500/10';
    default:
      return 'bg-gray-500/10';
  }
};

/**
 * Get status text label (Norwegian)
 */
const getStatusLabel = (status: Status): string => {
  switch (status) {
    case Status.Connected:
      return 'LIVE';
    case Status.Reconnecting:
      return 'Kobler til...';
    case Status.Disconnected:
      return 'Frakoblet';
    default:
      return 'Ukjent';
  }
};

/**
 * Get status icon component
 */
const getStatusIcon = (status: Status): React.ReactNode => {
  const iconClass = `w-5 h-5 ${getStatusColor(status)}`;
  
  switch (status) {
    case Status.Connected:
      return <Wifi className={iconClass} />;
    case Status.Reconnecting:
      return <RefreshCw className={`${iconClass} animate-spin`} />;
    case Status.Disconnected:
      return <WifiOff className={iconClass} />;
    default:
      return <WifiOff className={iconClass} />;
  }
};

/**
 * Format latency for display
 */
const formatLatency = (latency: number): string => {
  if (latency === 0) return '-';
  return `${latency}ms`;
};

/**
 * Get latency color class based on value
 */
const getLatencyColor = (latency: number): string => {
  if (latency === 0) return 'text-gray-500';
  if (latency < 100) return 'text-green-500';
  if (latency < 300) return 'text-yellow-500';
  return 'text-red-500';
};

/**
 * Get packet loss color class based on value
 */
const getPacketLossColor = (packetLoss: number): string => {
  if (packetLoss === 0) return 'text-gray-500';
  if (packetLoss < 5) return 'text-green-500';
  if (packetLoss < 15) return 'text-yellow-500';
  return 'text-red-500';
};

/**
 * ConnectionStatus Component
 */
export const ConnectionStatus: React.FC<ConnectionStatusProps> = ({ 
  status, 
  quality,
  className = '' 
}) => {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* Status Indicator with Icon */}
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${getStatusBgColor(status)}`}>
        {getStatusIcon(status)}
        <span className={`font-semibold text-sm ${getStatusColor(status)}`}>
          {getStatusLabel(status)}
        </span>
      </div>
      
      {/* Connection Quality Metrics - Only show when connected */}
      {status === Status.Connected && (
        <div className="flex items-center gap-4 text-xs text-gray-600">
          {/* Latency */}
          <div className="flex items-center gap-1.5">
            <span className="text-gray-500">Forsinkelse:</span>
            <span className={`font-medium ${getLatencyColor(quality.latency)}`}>
              {formatLatency(quality.latency)}
            </span>
          </div>
          
          {/* Packet Loss */}
          <div className="flex items-center gap-1.5">
            <span className="text-gray-500">Tap:</span>
            <span className={`font-medium ${getPacketLossColor(quality.packetLoss)}`}>
              {quality.packetLoss.toFixed(1)}%
            </span>
          </div>
        </div>
      )}
      
      {/* Reconnecting message */}
      {status === Status.Reconnecting && (
        <div className="text-xs text-gray-600">
          Prøver å gjenopprette tilkobling...
        </div>
      )}
      
      {/* Disconnected message */}
      {status === Status.Disconnected && (
        <div className="text-xs text-red-600 font-medium">
          Ingen tilkobling til sanntidsdata
        </div>
      )}
    </div>
  );
};

export default ConnectionStatus;
